import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpEventType,
  HttpHeaders,
  HttpResponse,
} from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { AuthResponse } from 'src/models/auth-response.model';
import { LocalStorageService } from './storage.service';
import { catchError, filter, lastValueFrom, map } from 'rxjs';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  apiUrl = environment.apiUrl;
  notificationUrl = environment.notificationService;

  public endpoints = {
    authenticate: this.apiUrl + '/auth',
    users: this.apiUrl + '/users',
    search: this.apiUrl + '/search',
    payment: this.apiUrl + '/payment',
    organizations: this.apiUrl + '/organizations',
    memberships: this.apiUrl + '/memberships',
    reports: this.apiUrl + '/report',
    registrations: this.apiUrl + '/rodeoregistrations',
    rodeos: this.apiUrl + '/rodeos',
    streaming: this.apiUrl + '/streaming',
    timer: this.apiUrl + '/timer',
    judges: this.apiUrl + '/judges',
    runs: this.apiUrl + '/rodeoEventRuns',
    points: this.apiUrl + '/points',
    pointSeries: this.apiUrl + '/PointSeries',
    images: this.apiUrl + '/images',
    rodeoEvents: this.apiUrl + '/rodeoEvents',
    expenseItems: this.apiUrl + '/rodeoExpenseItems',
    payoutconfigpointseries: this.apiUrl + '/payoutconfigpointseries',
    leaderboardconfigs: this.apiUrl + '/leaderboardconfigs',
    documents: this.apiUrl + '/documents',
    changeRequests: this.apiUrl + '/changeRequest',
    pointsRodeoNominations: this.apiUrl + '/nominationpoints',
    accountGroups: this.apiUrl + '/accountgroup',
    rodeoEventGroups: this.apiUrl + '/rodeoEventsGroups',
    texting: this.notificationUrl + '/notifications', // Sends emails, texts (uses LIVE HOST URL; No localhost)
    notification: this.apiUrl + '/notifications', // Default route for notifications
    notifications: this.apiUrl + '/api/register' // Gets new account push token... for ???
  };

  constructor(
    private http: HttpClient,
    private storage: LocalStorageService,
  ) { }

  public async get<T>(url: string, useAuth = true) {
    const headers = await this.getHeaders(useAuth);
    return lastValueFrom(this.http.get<T>(url, { headers }));
  }

  public async post<T>(url: string, body: any, useAuth = true, contentType = 'application/json') {
    const headers = await this.getHeaders(useAuth, contentType);
    return lastValueFrom(this.http.post<T>(url, body, { headers }));
  }

  public async postAndReportProgress<T>(
    url: string,
    body: FormData,
    useAuth = true,
  ) {
    const headers = await this.getHeaders(useAuth, '');
    return this.http
      .post<T>(url, body, {
        headers,
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        filter(
          (event) =>
            event.type === HttpEventType.UploadProgress ||
            event.type === HttpEventType.Response,
        ),
        map((event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            const progress = Math.round((100 * event.loaded) / event.total);
            return { status: 'progress', message: `${progress}%` };
          } else if (event instanceof HttpResponse) {
            return { status: 'complete', response: event.body };
          } else {
            return { status: '', message: '', response: '' };
          }
        }),
        catchError((error) => {
          return error;
        }),
      );
  }

  public async put<T>(url: string, body: any, useAuth = true) {
    const headers = await this.getHeaders(useAuth);
    return lastValueFrom(this.http.put<T>(url, body, { headers }));
  }

  public async delete<T>(url: string, useAuth = true) {
    const headers = await this.getHeaders(useAuth);
    return lastValueFrom(this.http.delete<T>(url, { headers }));
  }

  public async downloadFile(url: string) {
    const headers = await this.getHeaders(true);
    const response = await lastValueFrom(this.http.get(url, { responseType: 'blob', observe: 'response', headers }));
    const contentDisposition = response.headers.get('Content-Disposition');
    const filename = this.extractFilename(contentDisposition);

    if (response.body) {
      const downloadUrl = window.URL.createObjectURL(response.body);
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.style.display = 'none';
      a.href = downloadUrl;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      a.remove();
    }
  }

  async getFileContents(url: string, body: any = undefined) {
    let headers = await this.getHeaders(true)
    headers = headers.append('Content-Type', 'application/json');
    const options = { headers: headers, observe: 'response', responseType: 'blob' as 'json' } as any;
    const file = await lastValueFrom((body ? this.http.post<any>(url, body, options) : this.http.get<any>(url, options))
      .pipe(
        map((response: any) => {
          const contentType = response.body.type;
          const contentDisposition = response.headers.get('Content-Disposition');
          const filename = this.extractFilename(contentDisposition);
          return { data: response.body as any, contentType, filename };
        })
      ));

    this.saveAsFile(file.data, file.filename, file.contentType);
  }

  private extractFilename(contentDisposition: string | null): string {
    if (!contentDisposition) {
      return 'default-filename.csv'; //should probably use content-type to figure this out...
    }
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    return filenameMatch ? filenameMatch[1].replace(/['"]/g, '') : 'default-filename.csv';
  }

  public async getHeaders(useAuth: boolean, contentType = 'application/json') {
    const token = await this.storage.getEntity<AuthResponse>(
      this.storage.keys.token,
    );
    let headers = new HttpHeaders();
    headers = headers.append('AppVersion', environment.appVersion);
    if (contentType) {
      headers = headers.append('Content-Type', contentType);
    }
    if (token && useAuth) {
      headers = headers.append('Authorization', 'Bearer ' + token.accessToken);
    }
    return headers;
  }

  public saveAsFile(data: any, fileName: string, type: string): void {
    const blob = new Blob([data], { type });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(a.href);
    a.remove();
  }
}
